import React from "react";
import { observer } from "mobx-react";
import { MenuItem, Select, useTheme } from "@mui/material";
import { KeyboardArrowDownRounded } from "@mui/icons-material";
import {
    type IUserSelectedContext,
    UserSelectedContext,
    UserSelectedContextType,
} from "../Models/Context/UserSelectedContext";
import { useCurrentChatStore } from "../Stores/useCurrentChat";

export const SwitchContext: React.FC = observer(() => {
    const theme = useTheme();
    const currentChatStore = useCurrentChatStore();

    const currentContext = currentChatStore?.userSelectedContext;
    const isScopedContext =
        currentContext?.type !== UserSelectedContextType.AllConversations;
    const hasChartDataPointContext =
        currentChatStore?.hasChartDataPointContext ?? false;
    const hasProactiveInsightContext =
        currentChatStore?.hasProactiveInsightContext ?? false;

    return (
        <Select
            id="kbo-context"
            value={
                currentContext?.type ?? UserSelectedContextType.AllConversations
            }
            onChange={(e) => {
                const selectedType = e.target.value as UserSelectedContextType;
                let selectedContext: IUserSelectedContext =
                    UserSelectedContext.AllConversations;

                if (selectedType === UserSelectedContextType.AppliedFilters) {
                    selectedContext = UserSelectedContext.AppliedFilters;
                } else if (
                    selectedType === UserSelectedContextType.ChartDataPoint &&
                    hasChartDataPointContext
                ) {
                    selectedContext = UserSelectedContext.ChartDataPoint;
                } else if (
                    selectedType === UserSelectedContextType.ProactiveInsight &&
                    hasProactiveInsightContext
                ) {
                    selectedContext = UserSelectedContext.ProactiveInsight;
                }
                currentChatStore?.setUserSelectedContext(selectedContext);
            }}
            variant="outlined"
            sx={{
                borderRadius: 1,
                backgroundColor: isScopedContext
                    ? theme.palette.primary.main
                    : theme.palette.gray[100],
                color: isScopedContext
                    ? theme.palette.white.main
                    : theme.palette.gray[700],
                ".MuiSelect-icon": {
                    color: isScopedContext
                        ? theme.palette.white.main
                        : theme.palette.gray[700],
                },
                ".MuiOutlinedInput-notchedOutline": {
                    border: "none",
                },
                border: "none",
                fontSize: "14px",
                "& .MuiOutlinedInput-notchedOutline": {
                    border: "none",
                },
            }}
            MenuProps={{
                PaperProps: {
                    sx: {
                        mt: 1,
                        boxShadow: 3,
                    },
                },
            }}
            inputProps={{
                sx: {
                    paddingRight: "0px !important",
                },
            }}
            IconComponent={() => (
                <KeyboardArrowDownRounded
                    fontSize="small"
                    sx={{
                        backgroundColor: "transparent",
                        paddingRight: "0px",
                    }}
                />
            )}
        >
            <MenuItem
                value={UserSelectedContextType.AllConversations}
                sx={{
                    color: theme.palette.gray[700],
                }}
            >
                Context: {UserSelectedContext.AllConversations.label}
            </MenuItem>
            <MenuItem
                value={UserSelectedContextType.AppliedFilters}
                sx={{
                    color: theme.palette.gray[700],
                }}
            >
                Context: {UserSelectedContext.AppliedFilters.label}
            </MenuItem>
            {hasChartDataPointContext && (
                <MenuItem
                    value={UserSelectedContextType.ChartDataPoint}
                    sx={{
                        color: theme.palette.gray[700],
                    }}
                >
                    Context: {UserSelectedContext.ChartDataPoint.label}
                </MenuItem>
            )}
            {hasProactiveInsightContext && (
                <MenuItem
                    value={UserSelectedContextType.ProactiveInsight}
                    sx={{
                        color: theme.palette.gray[700],
                    }}
                >
                    Context: {UserSelectedContext.ProactiveInsight.label}
                </MenuItem>
            )}
        </Select>
    );
});
